import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Save, Percent, Truck, ShieldCheck, ToggleLeft, Mail, Building2,
  Image as ImageIcon, Loader2,
} from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { ImageSpec, IMAGE_SPECS, type ImageSpecKey } from '@/components/shared/ImageSpec';
import { adminApi } from '@/services/api';
import { cn } from '@nepal-ecom/ui';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Input } from '@nepal-ecom/ui';

type Section = 'general' | 'brand' | 'commission' | 'shipping' | 'kyc' | 'flags' | 'templates';

const SECTIONS: Array<{ key: Section; label: string; icon: React.ElementType; description: string }> = [
  { key: 'general',    label: 'General',          icon: Building2,   description: 'Platform identity and contact' },
  { key: 'brand',      label: 'Brand assets',     icon: ImageIcon,   description: 'Logo, favicon, OG image' },
  { key: 'commission', label: 'Commission',       icon: Percent,     description: 'Vendor tiers and listing fees' },
  { key: 'shipping',   label: 'Shipping zones',   icon: Truck,       description: 'Default rates per province' },
  { key: 'kyc',        label: 'KYC',              icon: ShieldCheck, description: 'Documents required per tier' },
  { key: 'flags',      label: 'Feature flags',    icon: ToggleLeft,  description: 'Roll out features safely' },
  { key: 'templates',  label: 'Email/SMS',        icon: Mail,        description: 'Notification templates' },
];

interface BrandAsset {
  key: string;
  spec: ImageSpecKey;
  description: string;
  aspect: string;     // tailwind aspect-* class for the preview
  preview: 'square' | 'wide' | 'banner';
}

const BRAND_ASSETS: BrandAsset[] = [
  { key: 'platformLogo', spec: 'platformLogo', description: 'Shown in the customer header and emails.',          aspect: 'aspect-[4/1]',  preview: 'wide' },
  { key: 'favicon',      spec: 'favicon',      description: 'Browser tab icon. Generate from a square PNG.',    aspect: 'aspect-square', preview: 'square' },
  { key: 'ogImage',      spec: 'ogImage',      description: 'Default social-share card when no page overrides.',aspect: 'aspect-[1.91/1]', preview: 'banner' },
  { key: 'promoBanner',  spec: 'promoBanner',  description: 'Top-of-homepage promo banner.',                    aspect: 'aspect-[32/9]', preview: 'banner' },
  { key: 'emailHeader',  spec: 'emailHeader',  description: 'Header strip on transactional emails.',            aspect: 'aspect-[3/1]',  preview: 'wide' },
];

interface Tier { name: string; commission: number; listingFee: number; }
interface Zone { province: string; standard: number; express: number; etaDays: string; }
interface FlagState { key: string; label: string; description: string; enabled: boolean; }

interface KycDoc { tier: string; docs: string[] }
interface NotificationTemplate { key: string; label: string; channel: string }
interface SettingsPayload {
  general: { name: string; supportEmail: string; supportPhone: string; currency: string; timezone: string };
  tiers: Tier[];
  zones: Zone[];
  flags: FlagState[];
  kycDocs: KycDoc[];
  notificationTemplates: NotificationTemplate[];
}

export function Settings() {
  const qc = useQueryClient();
  const [section, setSection] = useState<Section>('general');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-settings'],
    queryFn: () => adminApi.getPlatformSettings(),
    select: (res) => res.data.data as SettingsPayload,
  });

  const [general, setGeneral] = useState<SettingsPayload['general']>({
    name: '', supportEmail: '', supportPhone: '', currency: 'NPR', timezone: 'Asia/Kathmandu',
  });
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [flags, setFlags] = useState<FlagState[]>([]);

  useEffect(() => {
    if (!data) return;
    if (data.general) setGeneral(data.general);
    if (data.tiers) setTiers(data.tiers);
    if (data.zones) setZones(data.zones);
    if (data.flags) setFlags(data.flags);
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (payload: Partial<SettingsPayload>) => adminApi.updatePlatformSettings(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-settings'] });
      toast.success('Settings saved', {
        description: SECTIONS.find((s) => s.key === section)?.label,
      });
    },
    onError: (e: any) => {
      toast.error('Save failed', {
        description: e?.response?.data?.message ?? 'Could not save settings',
      });
    },
  });

  const save = () => {
    const payload: Partial<SettingsPayload> = {};
    if (section === 'general') payload.general = general;
    else if (section === 'commission') payload.tiers = tiers;
    else if (section === 'shipping') payload.zones = zones;
    else if (section === 'flags') payload.flags = flags;
    else {
      toast.info('Nothing to save in this section yet');
      return;
    }
    saveMutation.mutate(payload);
  };

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Platform Settings"
        description="Commission tiers, shipping rules, KYC requirements, and feature flags."
        actions={
          <Button onClick={save} size="sm" disabled={saveMutation.isPending || isLoading}>
            {saveMutation.isPending
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <Save className="h-4 w-4" />}
            Save changes
          </Button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
        <aside className="space-y-1">
          {SECTIONS.map(({ key, label, icon: Icon, description }) => (
            <button
              key={key}
              onClick={() => setSection(key)}
              className={cn(
                'w-full text-left rounded-lg px-3 py-2.5 transition-colors',
                section === key
                  ? 'bg-primary-50 ring-1 ring-primary-200'
                  : 'hover:bg-slate-100',
              )}
            >
              <div className="flex items-center gap-2.5">
                <Icon className={cn('h-4 w-4', section === key ? 'text-primary-600' : 'text-slate-500')} />
                <span className={cn('text-sm font-semibold', section === key ? 'text-primary-700' : 'text-slate-700')}>{label}</span>
              </div>
              <p className="text-[11px] text-slate-500 mt-0.5 ml-6.5">{description}</p>
            </button>
          ))}
        </aside>

        <div>
          {section === 'general' && (
            <Card>
              <CardHeader><CardTitle>General</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <Field label="Platform name">
                  <Input value={general.name} onChange={(e) => setGeneral({ ...general, name: e.target.value })} />
                </Field>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Support email">
                    <Input type="email" value={general.supportEmail} onChange={(e) => setGeneral({ ...general, supportEmail: e.target.value })} />
                  </Field>
                  <Field label="Support phone">
                    <Input value={general.supportPhone} onChange={(e) => setGeneral({ ...general, supportPhone: e.target.value })} />
                  </Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Currency">
                    <Input value={general.currency} onChange={(e) => setGeneral({ ...general, currency: e.target.value })} />
                  </Field>
                  <Field label="Timezone">
                    <Input value={general.timezone} onChange={(e) => setGeneral({ ...general, timezone: e.target.value })} />
                  </Field>
                </div>
              </CardContent>
            </Card>
          )}

          {section === 'brand' && (
            <Card>
              <CardHeader>
                <CardTitle>Brand assets</CardTitle>
                <p className="text-xs text-slate-500 mt-1">
                  All image sizes are enforced server-side. Uploads that don't match the dimensions are auto-cropped or rejected.
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {BRAND_ASSETS.map((asset) => (
                  <div key={asset.key} className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-4 items-start p-4 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors">
                    <div className={`${asset.aspect} rounded-lg ring-1 ring-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center`}>
                      <ImageIcon className="h-5 w-5 text-slate-300" />
                    </div>
                    <div className="min-w-0 space-y-2.5">
                      <div>
                        <p className="text-sm font-bold text-slate-900">{IMAGE_SPECS[asset.spec].label}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{asset.description}</p>
                      </div>
                      <ImageSpec spec={asset.spec} variant="card" />
                      <p className="text-[11px] text-slate-400">Brand asset upload coming soon.</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {section === 'commission' && (
            <Card>
              <CardHeader>
                <CardTitle>Vendor commission tiers</CardTitle>
                <p className="text-xs text-slate-500 mt-1">Commission % is taken from each successful order. Listing fee is monthly.</p>
              </CardHeader>
              <CardContent className="space-y-3">
                {tiers.map((t, i) => (
                  <div key={t.name} className="grid grid-cols-1 sm:grid-cols-[160px_1fr_1fr] gap-3 items-end p-3 rounded-lg border border-slate-200">
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Tier</p>
                      <p className="text-sm font-bold text-slate-900 mt-1">{t.name}</p>
                    </div>
                    <Field label="Commission (%)">
                      <Input type="number" min={0} max={100} value={t.commission} onChange={(e) => {
                        const v = Number(e.target.value);
                        setTiers((arr) => arr.map((x, j) => (j === i ? { ...x, commission: v } : x)));
                      }} />
                    </Field>
                    <Field label="Listing fee (NPR/mo)">
                      <Input type="number" min={0} value={t.listingFee} onChange={(e) => {
                        const v = Number(e.target.value);
                        setTiers((arr) => arr.map((x, j) => (j === i ? { ...x, listingFee: v } : x)));
                      }} />
                    </Field>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {section === 'shipping' && (
            <Card className="overflow-hidden">
              <CardHeader><CardTitle>Shipping zones (default)</CardTitle></CardHeader>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      {['Province', 'Standard (Rs.)', 'Express (Rs.)', 'ETA (days)'].map((h) => (
                        <th key={h} className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {zones.map((z, i) => (
                      <tr key={z.province}>
                        <td className="px-4 py-3 text-sm font-semibold text-slate-900">{z.province}</td>
                        <td className="px-4 py-3">
                          <Input type="number" min={0} value={z.standard} className="h-8 max-w-[120px]" onChange={(e) => {
                            const v = Number(e.target.value);
                            setZones((arr) => arr.map((x, j) => (j === i ? { ...x, standard: v } : x)));
                          }} />
                        </td>
                        <td className="px-4 py-3">
                          <Input type="number" min={0} value={z.express} className="h-8 max-w-[120px]" onChange={(e) => {
                            const v = Number(e.target.value);
                            setZones((arr) => arr.map((x, j) => (j === i ? { ...x, express: v } : x)));
                          }} />
                        </td>
                        <td className="px-4 py-3">
                          <Input value={z.etaDays} className="h-8 max-w-[100px]" onChange={(e) => {
                            const v = e.target.value;
                            setZones((arr) => arr.map((x, j) => (j === i ? { ...x, etaDays: v } : x)));
                          }} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}

          {section === 'kyc' && (
            <Card>
              <CardHeader>
                <CardTitle>KYC document requirements</CardTitle>
                <p className="text-xs text-slate-500 mt-1">Required per vendor tier before approval. Edit via PATCH /admin/settings &#123;"kycDocs": [...]&#125;.</p>
              </CardHeader>
              <CardContent className="space-y-3">
                {(data?.kycDocs ?? []).map((row) => (
                  <div key={row.tier} className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-lg border border-slate-200">
                    <p className="text-sm font-bold text-slate-900 min-w-[100px]">{row.tier}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {row.docs.map((d) => <Badge key={d} variant="secondary">{d}</Badge>)}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {section === 'flags' && (
            <Card>
              <CardHeader><CardTitle>Feature flags</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {flags.map((f, i) => (
                  <div key={f.key} className="flex items-center justify-between p-3 rounded-lg border border-slate-200">
                    <div className="min-w-0 pr-3">
                      <p className="text-sm font-semibold text-slate-900">{f.label}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{f.description}</p>
                    </div>
                    <button
                      onClick={() => {
                        setFlags((arr) => arr.map((x, j) => (j === i ? { ...x, enabled: !x.enabled } : x)));
                      }}
                      className={cn(
                        'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                        f.enabled ? 'bg-primary-600' : 'bg-slate-300',
                      )}
                    >
                      <span className={cn('inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform', f.enabled ? 'translate-x-6' : 'translate-x-1')} />
                    </button>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {section === 'templates' && (
            <Card>
              <CardHeader><CardTitle>Email & SMS templates</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {(data?.notificationTemplates ?? []).map((t) => (
                  <div key={t.key} className="flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:bg-slate-50">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{t.label}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{t.channel}</p>
                    </div>
                    <span className="text-[11px] text-slate-400">Template editor coming soon</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1.5">{label}</label>
      {children}
    </div>
  );
}
